import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

/**
 * An event created when a Medic prescibes paramedical care to a Patient.
 * It contains a field letting the Medic enter medical imaging exam manually.
 */
export default class ParamedicalCarePrescribedJournalEvent
  extends JournalEvent
  implements PrescriptionJournalEvent
{
  static override type = "paramedical-care-prescribed-journal-event";

  /**
   * provides the right info about the patient and its health center
   */
  @attr() declare snapshot: any;

  /**
   * The nature of the prescribed imaging or exam linked to the ALD
   */
  @attr("string") declare careAld: string;

  /**
   * The clinical state of the patient
   */
  @attr("string") declare careNonAld: string;

  @attr("string") declare subType: string;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.paramedical-care-prescribed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "paramedicalCarePrescribedJournalEvents"
  })
  declare patient: Patient;

  @attr("string")
  declare uuid: string;

  get startDate(): Date {
    return this.occurredAt;
  }

  get endDate(): Date {
    const endDate = new Date(this.startDate);
    endDate.setMonth(endDate.getMonth() + 12);

    return endDate;
  }

  async renew(): Promise<ParamedicalCarePrescribedJournalEvent> {
    const prescriptionRenewed = await this.store
      .createRecord("paramedical-care-prescribed-journal-event", {
        occurredAt: new Date(),
        patient: this.patient,
        careAld: this.careAld,
        careNonAld: this.careNonAld
      })
      .save();

    return prescriptionRenewed;
  }

  get pdfUrl() {
    return `/api/v1/paramedical_care_prescriptions/${this.id}`;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "paramedical-care-prescribed-journal-event": ParamedicalCarePrescribedJournalEvent;
  }
}
